import { useState, useCallback, useRef, useEffect, useMemo } from 'preact/hooks';
import type { FilterOption } from '../hooks/useFilters';

type ComboboxProps = {
	id: string;
	label: string;
	placeholder: string;
	options: FilterOption[];
	selected: string[];
	onChange: ( selected: string[] ) => void;
};

/**
 * Custom single-select combobox component.
 * Renders a button trigger that opens a dropdown of selectable options.
 * The selected option gets a colored background; hovered options get an underline.
 * Includes an "All" option that clears the selection.
 *
 * @param {Object} props - Component props.
 * @return {*} jsx
 */
export default function Combobox( {
	id,
	label,
	placeholder,
	options,
	selected,
	onChange,
}: ComboboxProps ) {
	const [ isOpen, setIsOpen ] = useState( false );
	const [ focusedIndex, setFocusedIndex ] = useState( -1 );
	const containerRef = useRef< HTMLDivElement >( null );
	const listboxRef = useRef< HTMLUListElement >( null );

	// Build display text for the trigger button.
	const displayText = selected.length > 0
		? options.find( ( o ) => o.value === selected[ 0 ] )?.value ?? selected[ 0 ]
		: placeholder;

	/**
	 * Check if an option is enabled (has matches, or is currently selected so it can be deselected).
	 */
	const isOptionEnabled = useCallback(
		( option: FilterOption ) => option.count > 0 || selected.includes( option.value ),
		[ selected ]
	);

	/**
	 * Visible option indices: 0 = "All" (always visible), then only enabled options.
	 * Keyboard navigation, scrollIntoView, and aria-activedescendant all use this list
	 * so they stay aligned with the actual DOM children.
	 */
	const visibleIndices = useMemo( () => {
		const indices = [ 0 ]; // "All" is always visible at position 0.
		options.forEach( ( option, i ) => {
			if ( isOptionEnabled( option ) ) {
				indices.push( i + 1 ); // offset by 1 for "All".
			}
		} );
		return indices;
	}, [ options, isOptionEnabled ] );

	/**
	 * Select a single option, or deselect if already selected.
	 */
	const toggleOption = useCallback(
		( value: string ) => {
			const next = selected.includes( value ) ? [] : [ value ];
			onChange( next );
			setIsOpen( false );
		},
		[ selected, onChange ]
	);

	/**
	 * Select the "All" option — clears all selections and closes the dropdown.
	 */
	const selectAll = useCallback( () => {
		onChange( [] );
		setIsOpen( false );
	}, [ onChange ] );

	/**
	 * Toggle dropdown open/closed.
	 */
	const handleTriggerClick = useCallback( () => {
		setIsOpen( ( prev ) => {
			if ( ! prev ) {
				setFocusedIndex( 0 );
			}
			return ! prev;
		} );
	}, [] );

	/**
	 * Close dropdown when clicking outside.
	 */
	useEffect( () => {
		if ( ! isOpen ) {
			return;
		}

		const handleClickOutside = ( e: MouseEvent ) => {
			if ( containerRef.current && ! containerRef.current.contains( e.target as Node ) ) {
				setIsOpen( false );
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => document.removeEventListener( 'mousedown', handleClickOutside );
	}, [ isOpen ] );

	/**
	 * Find the next visible option index in a direction.
	 * Navigates only through visibleIndices so hidden options are never focused.
	 */
	const findNextVisibleIndex = useCallback(
		( from: number, direction: 1 | -1 ): number => {
			const pos = visibleIndices.indexOf( from );
			const nextPos = pos + direction;
			if ( nextPos >= 0 && nextPos < visibleIndices.length ) {
				return visibleIndices[ nextPos ];
			}
			return from; // Stay put at the boundary.
		},
		[ visibleIndices ]
	);

	/**
	 * Keyboard navigation for the dropdown.
	 */
	const handleKeyDown = useCallback(
		( e: KeyboardEvent ) => {
			if ( ! isOpen ) {
				if ( e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ' ) {
					e.preventDefault();
					setIsOpen( true );
					setFocusedIndex( 0 );
				}
				return;
			}

			switch ( e.key ) {
				case 'ArrowDown':
					e.preventDefault();
					setFocusedIndex( ( prev ) => findNextVisibleIndex( prev, 1 ) );
					break;
				case 'ArrowUp':
					e.preventDefault();
					setFocusedIndex( ( prev ) => findNextVisibleIndex( prev, -1 ) );
					break;
				case 'Enter':
				case ' ':
					e.preventDefault();
					if ( focusedIndex === 0 ) {
						selectAll();
					} else if ( focusedIndex > 0 ) {
						toggleOption( options[ focusedIndex - 1 ].value );
					}
					break;
				case 'Escape':
					e.preventDefault();
					setIsOpen( false );
					break;
				case 'Tab':
					setIsOpen( false );
					break;
				case 'Home':
					e.preventDefault();
					setFocusedIndex( visibleIndices[ 0 ] );
					break;
				case 'End':
					e.preventDefault();
					setFocusedIndex( visibleIndices[ visibleIndices.length - 1 ] );
					break;
			}
		},
		[ isOpen, focusedIndex, options, toggleOption, selectAll, findNextVisibleIndex, visibleIndices ]
	);

	/**
	 * Scroll focused option into view.
	 */
	useEffect( () => {
		if ( ! isOpen || focusedIndex < 0 || ! listboxRef.current ) {
			return;
		}

		const focusedEl = listboxRef.current.querySelector( `#${ id }-option-${ focusedIndex }` ) as HTMLElement | null;
		focusedEl?.scrollIntoView( { block: 'nearest' } );
	}, [ isOpen, focusedIndex, id ] );

	const listboxId = `${ id }-listbox`;
	const activedescendant = focusedIndex >= 0 ? `${ id }-option-${ focusedIndex }` : undefined;
	const isAllSelected = selected.length === 0;

	return (
		<div className="str-maps__combobox" ref={ containerRef }>
			<label id={ `${ id }-label` } className="str-maps__filters-label">
				{ label }
			</label>
			<button
				type="button"
				id={ id }
				className={ `str-maps__combobox-trigger ${ isOpen ? 'is-open' : '' }` }
				role="combobox"
				aria-expanded={ isOpen }
				aria-haspopup="listbox"
				aria-controls={ isOpen ? listboxId : undefined }
				aria-labelledby={ `${ id }-label` }
				aria-activedescendant={ isOpen ? activedescendant : undefined }
				onClick={ handleTriggerClick }
				onKeyDown={ handleKeyDown }
			>
				<span className={ selected.length > 0 ? '' : 'str-maps__combobox-placeholder' }>
					{ displayText }
				</span>
				<svg
					className="str-maps__combobox-chevron"
					width="12"
					height="8"
					viewBox="0 0 12 8"
					fill="none"
					aria-hidden="true"
				>
					<path
						d="M1 1.5L6 6.5L11 1.5"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>

			{ isOpen && (
				<ul
					id={ listboxId }
					ref={ listboxRef }
					className="str-maps__combobox-listbox"
					role="listbox"
					aria-labelledby={ `${ id }-label` }
				>
					{ /* "All" option — selected when nothing else is */ }
					<li
						id={ `${ id }-option-0` }
						className={ `str-maps__combobox-option ${ focusedIndex === 0 ? 'is-focused' : '' } ${ isAllSelected ? 'is-selected' : '' }` }
						role="option"
						aria-selected={ isAllSelected }
						onClick={ ( e ) => {
							e.stopPropagation();
							selectAll();
							setFocusedIndex( 0 );
						} }
						onKeyDown={ ( e ) => {
							e.stopPropagation();
							selectAll();
							setFocusedIndex( 0 );
						} }
						onMouseEnter={ () => setFocusedIndex( 0 ) }
					>
						All
					</li>

					{ options.map( ( option, index ) => {
						const optionIndex = index + 1; // offset by 1 for "All" option.

						// Hide options with no matches.
						if ( ! isOptionEnabled( option ) ) {
							return null;
						}

						const isSelected = selected.includes( option.value );
						const isFocused = optionIndex === focusedIndex;

						return (
							<li
								key={ option.value }
								id={ `${ id }-option-${ optionIndex }` }
								className={ `str-maps__combobox-option ${ isFocused ? 'is-focused' : '' } ${ isSelected ? 'is-selected' : '' }` }
								role="option"
								aria-selected={ isSelected }
								onClick={ ( e ) => {
									e.stopPropagation();
									toggleOption( option.value );
									setFocusedIndex( optionIndex );
								} }
								onKeyDown={ ( e ) => {
									e.stopPropagation();
									toggleOption( option.value );
									setFocusedIndex( optionIndex );
								} }
								onMouseEnter={ () => setFocusedIndex( optionIndex ) }
							>
								{ option.label }
							</li>
						);
					} ) }
				</ul>
			) }
		</div>
	);
}
